import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useWebSocket } from '../hooks/useWebSocket'
import { useAuth } from '../hooks/useAuth'
import {
  MessageSquare,
  Clock,
  Send,
  UserCircle,
  Bot,
  Search,
  ChevronRight,
  Zap,
  Globe,
  ArrowLeft,
  Mail,
  Phone,
  AlertTriangle,
  X,
  Flag,
  Radio,
  History,
  Settings as SettingsIcon
} from 'lucide-react'
import api from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { formatDistanceToNow } from 'date-fns'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'

interface Visitor {
  visitorId: string
  sessionId?: string
  userAgent?: string
  url?: string
  timestamp: string
  hasAgent: boolean
  agentId?: string
  handlingState?: 'ai_handling' | 'agent_handling' | 'agent_stalled'
  assignedAgentId?: string
  lastActivityAt?: string
  contactInfo?: {
    firstName?: string
    email?: string
    phoneNumber?: string
    companyName?: string
  }
  messages: Message[]
}

interface Message {
  id: string
  content: string
  senderType: 'visitor' | 'agent' | 'bot' | 'system'
  timestamp: string
}

interface ChatPageProps {
  /** True when rendered inside ChatHubPage's tab — suppresses the local
      page header so we don't double up with the hub's PageHeader. */
  embedded?: boolean
}

export default function ChatPage({ embedded = false }: ChatPageProps = {}) {
  const { agent } = useAuth()
  const { toast } = useToast()

  const handleFlagMessage = async (sessionId: string, messageId: string, classification: string, visitorId?: string) => {
    try {
      await api.post('/api/chat/classify-message', { sessionId, messageId, classification })
      toast({
        title: 'Message flagged',
        description: `Marked as ${classification.replace('_', ' ')}.`,
        action: visitorId && classification !== 'off_topic' ? (
          <Button variant="destructive" size="sm" onClick={() => handleBlockVisitor(visitorId, classification)}>
            Block Visitor
          </Button>
        ) : undefined
      })
    } catch {
      toast({ title: 'Error', description: 'Failed to flag message.', variant: 'destructive' })
    }
  }

  const handleBlockVisitor = async (visitorId: string, reason: string) => {
    try {
      await api.post('/api/agent/blocked-visitors', { visitorId, reason })
      toast({ title: 'Visitor blocked', description: 'They will no longer receive AI responses.' })
    } catch {
      toast({ title: 'Error', description: 'Failed to block visitor.', variant: 'destructive' })
    }
  }
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  // WebSocket connection is managed by App.tsx - no need to connect here
  const { sendMessage: sendWsMessage, takeChat, endChat, visitors, connectionHealth } = useWebSocket()
  const [selectedVisitor, setSelectedVisitor] = useState<Visitor | null>(null)
  const [newMessage, setNewMessage] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [isTyping] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [showMobileChat, setShowMobileChat] = useState(false)
  const [endChatConfirmId, setEndChatConfirmId] = useState<string | null>(null)

  useEffect(() => {
    if (selectedVisitor) {
      const updated = visitors.find(v => v.visitorId === selectedVisitor.visitorId)
      if (updated) {
        setSelectedVisitor(updated)
      }
    }
  }, [visitors, selectedVisitor])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [selectedVisitor?.messages])

  // Deep-link: auto-select visitor from URL param (e.g. ?sessionId=abc)
  useEffect(() => {
    const sessionId = searchParams.get('sessionId')
    if (sessionId && visitors.length > 0 && !selectedVisitor) {
      const match = visitors.find(v => v.sessionId === sessionId || v.visitorId === sessionId)
      if (match) {
        setSelectedVisitor(match)
        setShowMobileChat(true)
        // Clean the URL param after selecting
        searchParams.delete('sessionId')
        setSearchParams(searchParams, { replace: true })
      }
    }
  }, [searchParams, visitors, selectedVisitor])

  const handleTakeOver = async (visitorId: string) => {
    const result = await takeChat(visitorId)
    if (!result.ok) {
      toast({
        title: 'Could not take over chat',
        description: result.reason,
        variant: 'destructive',
      })
    }
  }

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedVisitor) return
    const text = newMessage.trim()
    setNewMessage('')
    const result = await sendWsMessage(selectedVisitor.visitorId, text)
    if (!result.ok) {
      // Restore the draft so the user can retry without re-typing.
      setNewMessage(text)
      toast({
        title: 'Message not sent',
        description: result.reason,
        variant: 'destructive',
      })
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const filteredVisitors = visitors.filter(visitor => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      visitor.visitorId.toLowerCase().includes(q) ||
      visitor.contactInfo?.firstName?.toLowerCase().includes(q) ||
      visitor.contactInfo?.email?.toLowerCase().includes(q) ||
      visitor.contactInfo?.companyName?.toLowerCase().includes(q)
    )
  })

  // Sort: stalled first, then agent_handling, then ai_handling
  const sortedVisitors = [...filteredVisitors].sort((a, b) => {
    const priority: Record<string, number> = { agent_stalled: 0, agent_handling: 1, ai_handling: 2 }
    const aPriority = priority[a.handlingState || 'ai_handling'] ?? 2
    const bPriority = priority[b.handlingState || 'ai_handling'] ?? 2
    return aPriority - bPriority
  })

  const getInitials = (id: string) => {
    return id.slice(-2).toUpperCase()
  }

  const getVisitorUrl = (url?: string) => {
    if (!url) return 'Unknown page'
    try {
      const urlObj = new URL(url)
      return urlObj.pathname === '/' ? 'Homepage' : urlObj.pathname
    } catch {
      return url
    }
  }

  const handleVisitorSelect = (visitor: Visitor) => {
    setSelectedVisitor(visitor)
    setShowMobileChat(true)
  }

  const handleBackToList = () => {
    setShowMobileChat(false)
    setSelectedVisitor(null)
  }

  const handleEndChat = (visitorId: string) => {
    endChat(visitorId)
    setSelectedVisitor(null)
    setShowMobileChat(false)
  }

  return (
    <div className={embedded
      ? 'h-full flex flex-col'
      : 'h-[calc(100dvh-12rem-env(safe-area-inset-bottom))] md:h-[calc(100vh-4rem)] flex flex-col'
    }>
      {/* Header — only when rendered standalone. ChatHubPage owns the
          PageHeader when this is mounted in the tab hub. */}
      {!embedded && (
        <div className={`mb-4 md:mb-6 ${showMobileChat ? 'hidden md:block' : ''}`}>
          <h1 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight text-foreground">Live Chats</h1>
          <p className="text-sm text-muted-foreground mt-1">Monitor and manage visitor conversations</p>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 md:gap-6 flex-1 min-h-0">
        {/* Visitors Sidebar - hidden on mobile when viewing chat */}
        <div className={`w-full md:w-96 glass rounded-lg border flex flex-col overflow-hidden ${showMobileChat ? 'hidden md:flex' : 'flex'}`}>
          {/* Sidebar Header */}
          <div className="p-4 border-b bg-muted/30">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-brand-primary" />
                <h2 className="font-semibold text-foreground">Active Chats</h2>
                <Badge variant="secondary">{visitors.length}</Badge>
              </div>
              <div className="flex items-center gap-2">
                {/* "Offline" used to render bright red even when there were
                   active visitors visible right below it — staff misread that
                   as "the system is broken" when really it's just the
                   real-time socket that's down. While polling has caught up
                   and we have visitors loaded, fall back to a neutral
                   "Polling" indicator so the panel doesn't look broken. */}
                <div className={`w-2 h-2 rounded-full ${
                  connectionHealth === 'healthy' ? 'bg-green-500 animate-pulse' :
                  connectionHealth === 'degraded' ? 'bg-yellow-500 animate-pulse' :
                  visitors.length > 0 ? 'bg-amber-500' :
                  'bg-red-500'
                }`} />
                <span className="text-xs text-muted-foreground">
                  {connectionHealth === 'healthy' ? 'Live' :
                   connectionHealth === 'degraded' ? 'Slow' :
                   visitors.length > 0 ? 'Polling' :
                   'Offline'}
                </span>
              </div>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search visitors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Visitors List */}
          <ScrollArea className="flex-1">
            {sortedVisitors.length === 0 ? (
              <div className="px-6 py-10 text-center">
                <div className="relative mx-auto mb-4 h-14 w-14">
                  <div className="absolute inset-0 rounded-2xl bg-primary/5 flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-primary/50" />
                  </div>
                  {connectionHealth === 'healthy' && !searchQuery && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-background" />
                    </span>
                  )}
                </div>
                <p className="text-sm font-medium text-foreground/80">
                  {searchQuery ? 'No visitors match' : connectionHealth === 'healthy' ? 'Listening for visitors' : 'No active visitors'}
                </p>
                {!searchQuery && (
                  <p className="text-muted-foreground/70 text-xs mt-1.5 leading-relaxed">
                    {connectionHealth === 'healthy'
                      ? 'New chats land here in real time.'
                      : 'Reconnect — or install the widget from Settings → Widget.'}
                  </p>
                )}
              </div>
            ) : (
              <div className="p-2 space-y-2">
                {sortedVisitors.map((visitor) => (
                  <div
                    key={visitor.visitorId}
                    onClick={() => handleVisitorSelect(visitor)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedVisitor?.visitorId === visitor.visitorId
                        ? 'bg-brand-primary text-white shadow-md'
                        : 'hover:bg-muted border border-transparent hover:border-border'
                    } ${visitor.handlingState === 'agent_stalled' && selectedVisitor?.visitorId !== visitor.visitorId ? 'border-l-2 border-l-yellow-500' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarFallback className={selectedVisitor?.visitorId === visitor.visitorId ? 'bg-background text-brand-primary' : 'bg-brand-primary-light text-brand-primary'}>
                          {getInitials(visitor.visitorId)}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`font-medium text-sm truncate ${selectedVisitor?.visitorId === visitor.visitorId ? 'text-white' : 'text-foreground'}`}>
                            {visitor.contactInfo?.firstName
                              ? `${visitor.contactInfo.firstName}${visitor.contactInfo.companyName ? ` · ${visitor.contactInfo.companyName}` : ''}`
                              : 'Website Visitor'}
                          </span>
                          {visitor.handlingState === 'agent_stalled' ? (
                            <Badge className="bg-yellow-500 text-white text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Stalled
                            </Badge>
                          ) : visitor.hasAgent ? (
                            <Badge className="bg-green-500 text-white text-xs">
                              <UserCircle className="w-3 h-3 mr-1" />
                              You
                            </Badge>
                          ) : (
                            <Badge className="bg-brand-secondary text-white text-xs">
                              <Bot className="w-3 h-3 mr-1" />
                              AI
                            </Badge>
                          )}
                        </div>

                        {visitor.messages.length > 0 && (
                          <p className={`text-xs truncate ${selectedVisitor?.visitorId === visitor.visitorId ? 'text-white/80' : 'text-muted-foreground'}`}>
                            {visitor.messages[visitor.messages.length - 1].content}
                          </p>
                        )}

                        <div className={`flex items-center gap-2 mt-1 text-xs ${selectedVisitor?.visitorId === visitor.visitorId ? 'text-white/60' : 'text-muted-foreground'}`}>
                          <Clock className="w-3 h-3" />
                          {formatDistanceToNow(new Date(visitor.timestamp), { addSuffix: true })}
                          {visitor.url && (
                            <>
                              <span>•</span>
                              <Globe className="w-3 h-3" />
                              <span className="truncate">{getVisitorUrl(visitor.url)}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Chat Area - shown on mobile only when viewing chat */}
        <div className={`flex-1 glass rounded-lg border flex flex-col overflow-hidden ${showMobileChat ? 'flex' : 'hidden md:flex'}`}>
          {selectedVisitor ? (
            <>
              {/* Chat Header */}
              <div className="p-3 md:p-4 border-b bg-muted/30">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    {/* Back button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleBackToList}
                      className="h-10 w-10 sm:h-8 sm:w-8 flex-shrink-0"
                      aria-label="Back to chat list"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <Avatar className="w-8 h-8 md:w-10 md:h-10 flex-shrink-0">
                      <AvatarFallback className="bg-brand-primary text-white text-sm">
                        {getInitials(selectedVisitor.visitorId)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-foreground text-sm md:text-base truncate">
                        {selectedVisitor.contactInfo?.firstName
                          ? `${selectedVisitor.contactInfo.firstName}${selectedVisitor.contactInfo.companyName ? ` · ${selectedVisitor.contactInfo.companyName}` : ''}`
                          : 'Website Visitor'}
                      </h3>
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        <p className="text-xs text-muted-foreground/60 font-mono">
                          ID: {selectedVisitor.visitorId.slice(-8)}
                        </p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">Connected {formatDistanceToNow(new Date(selectedVisitor.timestamp), { addSuffix: true })}</span>
                        </p>
                        {selectedVisitor.contactInfo?.email && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{selectedVisitor.contactInfo.email}</span>
                          </p>
                        )}
                        {selectedVisitor.contactInfo?.phoneNumber && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="w-3 h-3 flex-shrink-0" />
                            <span>{selectedVisitor.contactInfo.phoneNumber}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
                    {selectedVisitor.handlingState === 'agent_stalled' ? (
                      <Badge className="bg-yellow-500 text-white text-xs">
                        <AlertTriangle className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                        <span className="hidden sm:inline">Response needed</span>
                        <span className="sm:hidden">Stalled</span>
                      </Badge>
                    ) : selectedVisitor.hasAgent ? (
                      <Badge className="bg-green-500 text-white text-xs">
                        <UserCircle className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                        <span className="hidden sm:inline">You're handling this</span>
                        <span className="sm:hidden">You</span>
                      </Badge>
                    ) : (
                      <Button
                        onClick={() => handleTakeOver(selectedVisitor.visitorId)}
                        className="bg-brand-primary hover:bg-brand-primary/90 text-xs md:text-sm h-8 md:h-9 px-2 md:px-4"
                      >
                        <Zap className="w-3 h-3 md:w-4 md:h-4 md:mr-2" />
                        <span className="hidden md:inline">Take Over from AI</span>
                        <span className="md:hidden ml-1">Take Over</span>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEndChat(selectedVisitor.visitorId)}
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      title="End chat"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-3 md:p-6 bg-muted/20">
                <div className="max-w-3xl mx-auto">
                  {selectedVisitor.messages.map((message, index) => {
                    const isAgent = message.senderType === 'agent'
                    const isBot = message.senderType === 'bot'
                    const isVisitor = message.senderType === 'visitor'

                    // Group consecutive messages from the same sender
                    const prevMessage = selectedVisitor.messages[index - 1]
                    const isFirstInGroup = !prevMessage || prevMessage.senderType !== message.senderType
                    const nextMessage = selectedVisitor.messages[index + 1]
                    const isLastInGroup = !nextMessage || nextMessage.senderType !== message.senderType

                    return (
                      <div
                        key={message.id}
                        className={`flex gap-3 ${isAgent ? 'flex-row-reverse' : 'flex-row'} ${isFirstInGroup ? 'mt-4' : 'mt-1'} animate-slide-in-left`}
                      >
                        {/* Avatar — only show on first message in group, otherwise spacer */}
                        {isFirstInGroup ? (
                          <Avatar className="w-8 h-8 flex-shrink-0">
                            {isVisitor ? (
                              <AvatarFallback className="bg-muted text-muted-foreground">
                                {getInitials(selectedVisitor.visitorId)}
                              </AvatarFallback>
                            ) : isBot ? (
                              <AvatarFallback className="bg-brand-secondary text-white">
                                <Bot className="w-4 h-4" />
                              </AvatarFallback>
                            ) : (
                              <AvatarImage src={agent?.avatarUrl} alt={agent?.name} />
                            )}
                            {isAgent && !agent?.avatarUrl && (
                              <AvatarFallback className="bg-brand-primary text-white">
                                {agent?.name?.charAt(0) || 'A'}
                              </AvatarFallback>
                            )}
                          </Avatar>
                        ) : (
                          <div className="w-8 flex-shrink-0" />
                        )}

                        <div className={`flex flex-col ${isAgent ? 'items-end' : 'items-start'} max-w-[85%] sm:max-w-[70%]`}>
                          {/* Sender name + timestamp — only show on first message in group */}
                          {isFirstInGroup && (
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-muted-foreground">
                                {isVisitor ? (selectedVisitor.contactInfo?.firstName || 'Website Visitor') : isBot ? 'AI Assistant' : agent?.name}
                              </span>
                            </div>
                          )}

                          <div className="flex items-center gap-1 group/msg">
                            <div
                              className={`px-4 py-2.5 rounded-2xl shadow-sm ${
                                isAgent
                                  ? `bg-brand-primary text-white ${isFirstInGroup ? 'rounded-tr-sm' : ''} ${isLastInGroup ? '' : 'rounded-br-sm'}`
                                  : isBot
                                  ? `bg-brand-secondary text-white ${isFirstInGroup ? 'rounded-tl-sm' : ''} ${isLastInGroup ? '' : 'rounded-bl-sm'}`
                                  : `bg-background text-foreground border ${isFirstInGroup ? 'rounded-tl-sm' : ''} ${isLastInGroup ? '' : 'rounded-bl-sm'}`
                              }`}
                            >
                              <p className="text-sm leading-relaxed">{message.content}</p>
                            </div>
                            {isVisitor && selectedVisitor && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="opacity-0 group-hover/msg:opacity-100 transition-opacity p-1 hover:text-destructive" title="Flag message" aria-label="Flag this message">
                                    <Flag className="h-3 w-3" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-40">
                                  <DropdownMenuItem onClick={() => selectedVisitor.sessionId && handleFlagMessage(selectedVisitor.sessionId, message.id, 'spam', selectedVisitor.visitorId)}>
                                    Spam
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => selectedVisitor.sessionId && handleFlagMessage(selectedVisitor.sessionId, message.id, 'off_topic', selectedVisitor.visitorId)}>
                                    Off Topic
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => selectedVisitor.sessionId && handleFlagMessage(selectedVisitor.sessionId, message.id, 'suspicious', selectedVisitor.visitorId)}>
                                    Suspicious
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>

                          {/* Timestamp — only show on last message in group */}
                          {isLastInGroup && (
                            <span className="text-xs text-muted-foreground/60 mt-1 px-1">
                              {formatDistanceToNow(new Date(message.timestamp), { addSuffix: true })}
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  })}

                  {isTyping && (
                    <div className="flex gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="bg-muted text-muted-foreground">
                          {getInitials(selectedVisitor.visitorId)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="bg-background border rounded-2xl rounded-tl-sm px-4 py-2.5 shadow-sm">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Message Input */}
              <div className="p-3 md:p-4 border-t bg-background">
                {selectedVisitor.hasAgent ? (
                  <div className="flex gap-2 items-end">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      className="flex-1 resize-none text-base"
                    />
                    <Button
                      variant="outline"
                      onClick={() => setEndChatConfirmId(selectedVisitor.visitorId)}
                      className="h-10 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground px-3"
                    >
                      End Chat
                    </Button>
                    <Button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || connectionHealth !== 'healthy'}
                      title={connectionHealth !== 'healthy' ? 'Reconnecting...' : undefined}
                      className="bg-brand-primary hover:bg-brand-primary/90 h-10 w-10 p-0 md:h-10 md:w-auto md:px-4"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-sm text-muted-foreground py-2">
                    <div className="flex items-center gap-2">
                      <Bot className="w-4 h-4 text-brand-secondary" />
                      <span className="text-center">AI is handling this conversation</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleTakeOver(selectedVisitor.visitorId)}
                        variant="outline"
                        size="sm"
                        disabled={connectionHealth !== 'healthy'}
                      >
                        Take Over <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEndChatConfirmId(selectedVisitor.visitorId)}
                        className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      >
                        End Chat
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* End Chat Confirmation Dialog */}
              <Dialog open={endChatConfirmId !== null} onOpenChange={(open) => { if (!open) setEndChatConfirmId(null) }}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>End this conversation?</DialogTitle>
                    <DialogDescription>
                      This will mark the chat as completed. The visitor will no longer be in your live chats.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setEndChatConfirmId(null)}>
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        if (endChatConfirmId) handleEndChat(endChatConfirmId)
                        setEndChatConfirmId(null)
                      }}
                    >
                      End Chat
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          ) : (
            (() => {
              const aiHandling = visitors.filter(v => !v.handlingState || v.handlingState === 'ai_handling').length
              const youHandling = visitors.filter(v => v.handlingState === 'agent_handling' && v.assignedAgentId === agent?.id).length
              const stalled = visitors.filter(v => v.handlingState === 'agent_stalled').length
              const isLive = connectionHealth === 'healthy'
              const isDegraded = connectionHealth === 'degraded'

              return (
                <div className="flex-1 flex items-center justify-center px-6 py-8 overflow-y-auto bg-gradient-to-br from-background via-background to-muted/30">
                  <div className="w-full max-w-md">
                    {/* Stacked browser-window illustration */}
                    <div className="relative mx-auto mb-7 h-36 w-56">
                      {/* Back card */}
                      <div className="absolute left-0 top-4 h-24 w-44 rounded-xl border border-border/60 bg-muted shadow-sm opacity-60 overflow-hidden">
                        <div className="h-3.5 bg-card border-b border-border/60 flex items-center gap-1 px-2">
                          <span className="h-1 w-1 rounded-full bg-foreground/20" />
                          <span className="h-1 w-1 rounded-full bg-foreground/20" />
                          <span className="h-1 w-1 rounded-full bg-foreground/20" />
                        </div>
                        <div className="p-2 space-y-1.5">
                          <span className="block h-1.5 w-20 rounded-full bg-foreground/15" />
                          <span className="block h-1.5 w-28 rounded-full bg-foreground/10" />
                        </div>
                      </div>
                      {/* Front card with live indicator */}
                      <div className="absolute right-0 bottom-0 h-28 w-48 rounded-xl border border-border bg-card shadow-lg overflow-hidden">
                        <div className="h-4 bg-muted/60 border-b border-border flex items-center gap-1 px-2">
                          <span className="h-1.5 w-1.5 rounded-full bg-red-400/60" />
                          <span className="h-1.5 w-1.5 rounded-full bg-yellow-400/60" />
                          <span className="h-1.5 w-1.5 rounded-full bg-green-400/60" />
                          <span className="ml-auto h-1 w-12 rounded-full bg-foreground/15" />
                        </div>
                        <div className="p-3 space-y-2">
                          <div className="flex items-end gap-2">
                            <div className="h-5 w-5 rounded-full bg-gradient-to-br from-primary/80 to-primary shrink-0" />
                            <div className="rounded-lg rounded-bl-sm bg-muted px-2 py-1.5 max-w-[110px]">
                              <span className="block h-1 w-16 rounded-full bg-foreground/30" />
                            </div>
                          </div>
                          <div className="flex items-end gap-2 justify-end">
                            <div className="rounded-lg rounded-br-sm bg-primary px-2 py-1.5 max-w-[110px]">
                              <span className="block h-1 w-12 rounded-full bg-primary-foreground/50 mb-1" />
                              <span className="block h-1 w-8 rounded-full bg-primary-foreground/40" />
                            </div>
                          </div>
                        </div>
                        {/* Pulsing live dot in corner */}
                        {isLive && (
                          <span className="absolute top-1 right-1 flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-center mb-6">
                      <h2 className="text-xl font-semibold text-foreground">
                        {isLive ? 'Watching for visitors' : isDegraded ? 'Reconnecting…' : 'Live monitor offline'}
                      </h2>
                      <p className="text-sm text-muted-foreground mt-1.5">
                        {visitors.length > 0
                          ? 'Pick a visitor on the left to jump into the conversation.'
                          : isLive
                            ? 'Real-time chats appear here the moment someone interacts with your widget.'
                            : 'We\'ll resume streaming visitor conversations as soon as the connection comes back.'}
                      </p>
                    </div>

                    {/* Live status pill + counts */}
                    <div className="grid grid-cols-3 gap-2 mb-5">
                      <div className="rounded-xl border bg-card text-card-foreground shadow-sm card-inset p-3 text-center">
                        <div className="flex items-center justify-center gap-1.5 mb-1">
                          <Radio className={`h-3 w-3 ${isLive ? 'text-green-500' : isDegraded ? 'text-yellow-500' : 'text-muted-foreground/60'}`} />
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Active</span>
                        </div>
                        <p className="text-xl font-bold text-foreground tabular-nums">{visitors.length}</p>
                      </div>
                      <div className="rounded-xl border bg-card text-card-foreground shadow-sm card-inset p-3 text-center">
                        <div className="flex items-center justify-center gap-1.5 mb-1">
                          <Bot className="h-3 w-3 text-muted-foreground" />
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">AI</span>
                        </div>
                        <p className="text-xl font-bold text-foreground tabular-nums">{aiHandling}</p>
                      </div>
                      <div className="rounded-xl border bg-card text-card-foreground shadow-sm card-inset p-3 text-center">
                        <div className="flex items-center justify-center gap-1.5 mb-1">
                          <UserCircle className={`h-3 w-3 ${stalled > 0 ? 'text-yellow-500' : 'text-muted-foreground'}`} />
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{stalled > 0 ? 'Stalled' : 'You'}</span>
                        </div>
                        <p className={`text-xl font-bold tabular-nums ${stalled > 0 ? 'text-yellow-600 dark:text-yellow-400' : 'text-foreground'}`}>
                          {stalled > 0 ? stalled : youHandling}
                        </p>
                      </div>
                    </div>

                    {/* Quick actions */}
                    <div className="space-y-2">
                      <button
                        onClick={() => navigate('/conversations')}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border bg-card hover:border-primary/40 hover:bg-primary/[0.02] transition-colors text-left card-inset group"
                      >
                        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <History className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground">Browse past conversations</p>
                          <p className="text-xs text-muted-foreground">Search transcripts, lead notes, and outcomes</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </button>
                      <button
                        onClick={() => navigate('/widget')}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-border bg-transparent hover:border-primary/50 hover:bg-primary/[0.02] transition-colors text-left card-inset"
                      >
                        <div className="h-9 w-9 rounded-full bg-violet-500/10 flex items-center justify-center shrink-0">
                          <SettingsIcon className="h-4 w-4 text-violet-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground">Configure the widget</p>
                          <p className="text-xs text-muted-foreground">Embed code, branding, and proactive triggers</p>
                        </div>
                      </button>
                    </div>

                    <div className="flex items-center justify-center gap-1.5 mt-6 text-[11px] text-muted-foreground/70">
                      <Zap className="h-3 w-3" />
                      <span>You'll be paged the moment a visitor needs a human</span>
                    </div>
                  </div>
                </div>
              )
            })()
          )}
        </div>
      </div>
    </div>
  )
}